import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Stack,
  Chip,
  Alert,
  Skeleton,
  Divider,
} from '@mui/material';
import { ArrowBack, Edit, Business } from '@mui/icons-material';
import { useEmpresa } from '../../hooks/useEmpresas';
import { useAuth } from '../../context/AuthContext';
import {
  ESTADO_EMPRESA_LABELS,
  ESTADO_EMPRESA_COLORS,
  CONDICION_IVA_LABELS,
  type CondicionIva,
} from '../../types/empresa';
import { formatCuit, formatDate } from '../../utils/format';

interface FieldRowProps {
  label: string;
  value: React.ReactNode;
}

function FieldRow({ label, value }: FieldRowProps) {
  return (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} sx={{ minHeight: 24 }}>
        {value || '—'}
      </Typography>
    </Grid>
  );
}

export default function EmpresaDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { effectiveRole, startImpersonation } = useAuth();
  const { data: empresa, isLoading, isError, error } = useEmpresa(id);

  // Cuando hay impersonacion, el admin "no es admin" en pantalla:
  //  - no puede impersonar otra empresa desde adentro
  //  - puede editar SOLO su empresa (RLS lo controla a nivel BD igualmente)
  const canImpersonate = effectiveRole === 'admin_general';
  const canEdit = effectiveRole === 'admin_general' || effectiveRole === 'operador';

  if (isLoading) {
    return (
      <Card sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={40} width="60%" />
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={28} />
          ))}
        </Stack>
      </Card>
    );
  }

  if (isError) {
    return (
      <Alert severity="error">
        Error al cargar la empresa: {(error as Error)?.message}
      </Alert>
    );
  }

  if (!empresa) {
    return (
      <Alert severity="warning">
        Empresa no encontrada.{' '}
        <Button size="small" onClick={() => navigate('/empresas')}>
          Volver al listado
        </Button>
      </Alert>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/empresas')}
            variant="text"
          >
            Volver
          </Button>
          <Box sx={{ ml: 2 }}>
            <Typography variant="h5" fontWeight={700}>
              {empresa.razon_social}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontFamily: 'monospace' }}
            >
              CUIT {formatCuit(empresa.cuit)}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1}>
          {canImpersonate && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<Business />}
              onClick={() => {
                startImpersonation(empresa.id);
                navigate('/');
              }}
            >
              Ver como empresa
            </Button>
          )}
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => navigate(`/empresas/${empresa.id}/editar`)}
            >
              Editar
            </Button>
          )}
        </Stack>
      </Stack>

      <Card>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Chip
              label={ESTADO_EMPRESA_LABELS[empresa.estado]}
              color={ESTADO_EMPRESA_COLORS[empresa.estado]}
            />
            {empresa.convenio_2_porc && (
              <Chip label="Convenio 2%" color="primary" variant="outlined" />
            )}
          </Stack>

          <Typography variant="overline" color="text.secondary">
            Datos fiscales
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <FieldRow
              label="Condicion IVA"
              value={
                empresa.condicion_iva
                  ? CONDICION_IVA_LABELS[empresa.condicion_iva as CondicionIva]
                  : null
              }
            />
            <FieldRow
              label="Fecha inicio actividades"
              value={formatDate(empresa.fecha_inicio_actividades)}
            />
            <FieldRow label="Domicilio fiscal" value={empresa.domicilio} />
            <FieldRow label="Fecha alta en sistema" value={formatDate(empresa.fecha_alta)} />
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="overline" color="text.secondary">
            Contacto
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <FieldRow label="Telefono" value={empresa.telefono} />
            <FieldRow label="Email" value={empresa.email} />
          </Grid>

          {empresa.observaciones && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="overline" color="text.secondary">
                Observaciones
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                {empresa.observaciones}
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
